/* AddImagesRenderer.js */

"use strict";
const escapeHtml = require("@gluenetworks/dom_utilities").escapeHtml;
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .InputRadio;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const SingleComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const MultipleComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection
    .Multiple.ComboBox;
const InputFileControl = require("@gluenetworks/steve_ui_core").Control.Action.InputFile;
const FancyLoadingMask = require("@gluenetworks/fancy_loading_mask_gl");

/**
 * Renderer for the Add Images veil.
 * Implements PE-13464: Add Images to IoT Platform Explorer
 *
 * Features:
 * - Two modes: "Create a new Image" and "Select existing Images"
 * - Create mode: Name, Description, Type dropdown (Download URL/External/Upload File), URL field
 * - Select mode: Multi-select combobox with search
 * - Dynamic validation based on mode and Type selection
 * - Supports both editor mode (single entity) and explorer mode (array of IDs)
 */
class AddImagesRenderer {
    constructor(model, selectedTargets, isEditorMode) {
        this._model = model;
        this._selectedTargets = selectedTargets;
        this._isEditorMode = isEditorMode;
        this._rootElem = document.createElement("platform-add-images-gl");
        this._rootElem.component = this._model;

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = "Add Images";

        this._contentContainer = document.createElement("content-container");

        // Create mode selection radio buttons
        this._modeSelectionContainer = document.createElement("mode-selection-container");
        const modeOptions = [
            {
                label: "Add Image",
                value: "create",
            },
        ];

        // Only show "Select existing" option if there are existing images
        if (this._model.getOsLinkOptions().length > 0) {
            modeOptions.push({
                label: "Select existing Images",
                value: "add",
            });
        }

        this._modeRadio = new InputRadioControl({
            darkTheme: true,
            horizontal: true,
            options: modeOptions,
            value: "create",
        });
        this._currentMode = "create";

        // Create form section for "Create new" mode
        this._formSection = document.createElement("form-section");

        this._nameField = new InputTextControl({
            darkTheme: true,
            value: "",
            placeholder: "Enter Image Name",
        });

        this._descriptionField = new InputTextControl({
            darkTheme: true,
            value: "",
            placeholder: "Enter Image Description (optional)",
        });

        this._typeDropdown = new SingleComboBoxControl({
            darkTheme: true,
            options: [
                {label: "Download URL", value: "download"},
                {label: "External", value: "external"},
                {label: "Upload File", value: "upload"},
            ],
            value: "download",
        });
        this._currentType = "download";

        this._urlField = new InputTextControl({
            darkTheme: true,
            value: "",
            placeholder: "Enter Image URL (starting with http:// or https://)",
        });

        // File upload controls (similar to AddInstructions)
        this._fileUploadSection = document.createElement("file-upload-section");
        this._fileUploadSection.style.display = "none";

        this._fileDropArea = document.createElement("file-drop-area");
        this._fileDropArea.setAttribute("draggable", true);

        this._dropAreaContent = document.createElement("drop-area-content");
        this._plusIcon = document.createElement("plus-icon");
        this._plusIcon.innerHTML = "+";
        this._dropAreaText = document.createElement("drop-area-text");
        this._dropAreaText.innerText = "Drop Image Files to Upload";

        this._fileInputControl = new InputFileControl({
            id: "platformAddImagesFileUpload",
            label: "Select Image File",
            acceptFiles: "*",
            darkTheme: true,
            multiple: false,
        });

        this._browseRow = document.createElement("browse-row");
        this._browseRow.style.display = "none";

        this._uploadProgressContainer = document.createElement("upload-progress-container");
        this._uploadProgressContainer.style.display = "none";

        this._loadingMask = new FancyLoadingMask({
            darkTheme: true,
            shown: false,
        });

        this._selectedFile = null;
        this._isUploading = false;

        // Error container for validation messages
        this._errorContainer = document.createElement("error-container");
        this._errorContainer.style.display = "none";

        // Create "Add existing" section with multi-select
        this._addExistingSection = document.createElement("add-existing-section");
        this._addExistingSection.style.display = "none";

        this._existingImagesComboBox = new MultipleComboBoxControl({
            darkTheme: true,
            placeholder: "Search and select Images…",
            options: this._model.getOsLinkOptions(),
        });

        this._buttonContainer = document.createElement("button-container");
        this._buttonContainerInner = document.createElement("button-container-inner");

        this._cancelUploadButton = new ButtonControl({
            label: "Cancel Upload",
            darkTheme: true,
        });
        this._cancelUploadButton.renderer.rootElement.style.display = "none";

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._saveButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            theme: "active",
        });

        // Bind all event handlers in constructor
        this._onSaveButtonClick = this._onSaveButtonClick.bind(this);
        this._onCancelUploadButtonClick = this._onCancelUploadButtonClick.bind(this);
        this._onCancelButtonClick = this._onCancelButtonClick.bind(this);
        this._onModeChange = this._onModeChange.bind(this);
        this._onTypeChange = this._onTypeChange.bind(this);
        this._onFileSelected = this._onFileSelected.bind(this);
        this._onFileDrop = this._onFileDrop.bind(this);
        this._onDragOver = this._onDragOver.bind(this);
        this._onDragLeave = this._onDragLeave.bind(this);
        this._onUploadProgress = this._onUploadProgress.bind(this);
        this._onUploadReset = this._onUploadReset.bind(this);
        this._onShowError = this._onShowError.bind(this);
        this._boundValidateForm = this._validateForm.bind(this);

        this._registerListeners();
    }

    _registerListeners() {
        this._saveButton.on("UserClick", this._onSaveButtonClick);
        this._cancelButton.on("UserClick", this._onCancelButtonClick);
        this._cancelUploadButton.on("UserClick", this._onCancelUploadButtonClick);
        this._modeRadio.on("UserValue", this._onModeChange);
        this._typeDropdown.on("UserValue", this._onTypeChange);
        this._fileInputControl.on("FileHandle", this._onFileSelected);
        this._model.on("UploadProgress", this._onUploadProgress);
        this._model.on("ShowError", this._onShowError);
        this._model.on("UploadReset", this._onUploadReset);
        this._fileDropArea.addEventListener("drop", this._onFileDrop);
        this._fileDropArea.addEventListener("dragover", this._onDragOver);
        this._fileDropArea.addEventListener("dragleave", this._onDragLeave);
        this._nameField.on("UserValue", this._boundValidateForm);
        this._urlField.on("UserValue", this._boundValidateForm);
        this._existingImagesComboBox.on("UserValue", this._boundValidateForm);
    }

    _unregisterListeners() {
        this._saveButton.removeAllListeners("UserClick");
        this._cancelUploadButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._modeRadio.removeAllListeners("UserValue");
        this._typeDropdown.removeAllListeners("UserValue");
        this._fileInputControl.removeAllListeners("FileHandle");
        this._model.removeAllListeners("UploadProgress");
        this._model.removeAllListeners("ShowError");
        this._model.removeAllListeners("UploadReset");
        this._fileDropArea.removeEventListener("drop", this._onFileDrop);
        this._fileDropArea.removeEventListener("dragover", this._onDragOver);
        this._fileDropArea.removeEventListener("dragleave", this._onDragLeave);
        this._nameField.removeAllListeners("UserValue");
        this._urlField.removeAllListeners("UserValue");
        this._existingImagesComboBox.removeAllListeners("UserValue");
        this._model.removeListener("ShowError", this._onShowError);
    }

    _onCancelButtonClick() {
        this._model.closeAction();
    }

    _onCancelUploadButtonClick() {
        this._model.abortUpload();
        this._resetUploadUI();
    }

    /**
     * Handles Save button click.
     * PE-13464: Validates and saves based on current mode.
     *
     * Create mode:
     * - Download URL: Validates URL format and creates image with URL
     * - External: Creates image with EXTERNAL linkStatus
     * - Upload File: Creates entity and uploads file
     *
     * Select mode:
     * - Links selected existing images to selected platforms
     */
    _onSaveButtonClick() {
        if (this._currentMode === "create") {
            const type = this._currentType;
            const name = this._nameField.getValue();
            const url = this._urlField.getValue();

            // Validate based on type
            if (type === "download") {
                const hasName = name && name.trim().length > 0;
                const hasUrl = url && url.trim().length > 0;
                const isUrlValid = hasUrl && this._validateUrl(url);

                // Check all combinations and show appropriate error
                if (!hasName && !hasUrl) {
                    this._showError(
                        "Enter a Name and a valid URL starting with http:// or https://."
                    );
                    return;
                } else if (!hasName && hasUrl) {
                    this._showError("Enter a name.");
                    return;
                } else if (hasName && !hasUrl) {
                    this._showError("Enter a valid URL starting with http:// or https://.");
                    return;
                } else if (hasName && hasUrl && !isUrlValid) {
                    this._showError("Enter a valid URL starting with http:// or https://.");
                    return;
                }
            } else if (type === "external") {
                // External only requires name
                if (!name || name.trim().length === 0) {
                    this._showError("Enter a name.");
                    return;
                }
            } else if (type === "upload") {
                // Upload requires name and file
                const hasFile = this._selectedFile !== null;
                if (!name || name.trim().length === 0) {
                    this._showError("Enter a name.");
                    return;
                }
                if (!hasFile) {
                    this._showError("Select a file to upload.");
                    return;
                }
            }

            // All validations passed, proceed with save
            if (type === "upload") {
                // Handle file upload - create entity and upload file
                const imageData = {
                    name: this._nameField.getValue(),
                    description: this._descriptionField.getValue(),
                };

                // Show upload progress UI
                this._isUploading = true;
                this._uploadProgressContainer.style.display = "block";
                this._loadingMask.show();
                this._fileDropArea.style.display = "none";
                this._browseRow.style.display = "none";
                this._cancelButton.renderer.rootElement.style.display = "none";
                this._saveButton.renderer.rootElement.style.display = "none";
                this._cancelUploadButton.renderer.rootElement.style.display = "";

                this._model.uploadImageFile(imageData, this._selectedFile);
            } else {
                const formData = {
                    name: this._nameField.getValue(),
                    description: this._descriptionField.getValue(),
                    linkStatus: type === "external" ? "EXTERNAL" : "NEW",
                    url: type === "download" ? url : undefined,
                };

                this._model.createImage(formData);
            }
        } else {
            const selectedImages = this._existingImagesComboBox.getValue();
            if (!selectedImages || selectedImages.length === 0) {
                this._showError("Select at least one image.");
                return;
            }
            this._model.linkExistingImages(selectedImages);
        }
    }

    _onModeChange(val) {
        const newMode = val;
        this._currentMode = newMode;

        if (newMode === "create") {
            this._formSection.style.display = "";
            this._addExistingSection.style.display = "none";
            // Restore browse row visibility based on current type
            if (this._currentType === "upload") {
                this._browseRow.style.display = "";
            }
        } else {
            this._formSection.style.display = "none";
            this._addExistingSection.style.display = "";
            this._browseRow.style.display = "none";
        }

        this._hideError();
        this._validateForm();
    }

    /**
     * Handles Type dropdown change.
     * - Download URL: Shows URL field, hides file upload
     * - External: Hides URL field and file upload
     * - Upload File: Hides URL field, shows file upload with browse row
     */
    _onTypeChange(val) {
        this._currentType = val;

        // Show/hide fields based on type
        if (val === "download") {
            this._urlFieldRow.style.display = "";
            this._fileUploadSection.style.display = "none";
            this._saveButton.renderer.rootElement.style.display = "";
            this._browseRow.style.display = "none";
            this._resetUploadUI();
        } else if (val === "external") {
            this._urlFieldRow.style.display = "none";
            this._fileUploadSection.style.display = "none";
            this._saveButton.renderer.rootElement.style.display = "";
            this._browseRow.style.display = "none";
            this._resetUploadUI();
        } else if (val === "upload") {
            this._urlFieldRow.style.display = "none";
            this._fileUploadSection.style.display = "";
            this._saveButton.renderer.rootElement.style.display = "";
            this._browseRow.style.display = "";
            this._resetUploadUI();
        }

        this._hideError();
        this._validateForm();
    }

    /**
     * Handles file selection from file input control.
     */
    _onFileSelected(event) {
        event.preventDefault();
        const files = event.target.files;
        if (files && files.length > 0) {
            this._handleFileSelection(files[0]);
        }
    }

    /**
     * Handles file drop event.
     */
    _onFileDrop(event) {
        event.preventDefault();
        this._fileDropArea.classList.remove("drag-over");

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            this._handleFileSelection(files[0]);
        }
    }

    /**
     * Handles drag over event.
     */
    _onDragOver(event) {
        event.preventDefault();
        this._fileDropArea.classList.add("drag-over");
    }

    /**
     * Handles drag leave event.
     */
    _onDragLeave(event) {
        event.preventDefault();
        this._fileDropArea.classList.remove("drag-over");
    }

    /**
     * Handles file selection from either file input or drag/drop.
     */
    _handleFileSelection(file) {
        this._selectedFile = file;
        this._dropAreaText.innerHTML = `Selected: ${escapeHtml(
            file.name
        )}<br/><span class="file-size">(${this._formatFileSize(file.size)})</span>`;
        this._hideError();
        this._validateForm();
    }

    /**
     * Formats file size for display.
     */
    _formatFileSize(bytes) {
        if (bytes < 1024) {
            return bytes + " B";
        }
        if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(2) + " KB";
        }
        return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    }

    /**
     * Handles upload progress events.
     */
    _onUploadProgress(bytesUploaded, bytesTotal, percentage) {
        this._loadingMask.showProgress(`Uploading... ${percentage.toFixed(1)}%`);
    }

    /**
     * Handles upload reset event from component.
     * Called when upload fails, is aborted, or entity creation fails.
     */
    _onUploadReset() {
        this._resetUploadUI();
    }

    _onShowError(errorMsg) {
        this._showError(errorMsg);
        if (this._isUploading) {
            this._resetUploadUI();
        }
    }

    /**
     * Resets upload UI to initial state.
     */
    _resetUploadUI() {
        if (this._isUploading) {
            this._loadingMask.hide();
        }
        this._isUploading = false;
        this._selectedFile = null;
        this._uploadProgressContainer.style.display = "none";
        this._loadingMask.showProgress("Uploading... 0%");
        this._fileDropArea.style.display = "";
        this._fileInputControl.setDisabled(false);
        this._fileInputControl.setValue("");
        this._fileDropArea.classList.remove("uploading");
        this._dropAreaText.innerText = "Drop Image File to Upload";
        this._cancelUploadButton.renderer.rootElement.style.display = "none";
        this._cancelButton.renderer.rootElement.style.display = "";
        this._saveButton.renderer.rootElement.style.display = "";
        this._browseRow.style.display = this._currentType === "upload" ? "" : "none";
        this._model.resetUploadState();
        this._validateForm();
    }

    /**
     * Validates form and enables/disables Save button.
     */
    _validateForm() {
        // Save button is always enabled - validation happens on click
    }

    /**
     * Checks if form is valid based on current mode.
     * Create mode: Name required, URL for download, file for upload
     * Select mode: At least one image selected
     */
    _isFormValid() {
        if (this._currentMode === "create") {
            const name = this._nameField.getValue();
            if (!name || name.trim().length === 0) {
                return false;
            }

            if (this._currentType === "download") {
                const url = this._urlField.getValue();
                if (!url || url.trim().length === 0) {
                    return false;
                }
                // Basic URL validation
                if (!url.startsWith("http://") && !url.startsWith("https://")) {
                    return false;
                }
            } else if (this._currentType === "upload") {
                if (!this._selectedFile) {
                    return false;
                }
            }

            return true;
        } else if (this._currentMode === "add") {
            const selected = this._existingImagesComboBox.getValue();
            return selected && selected.length > 0;
        }

        return false;
    }

    /**
     * Validates URL format (must start with http:// or https://).
     */
    _validateUrl(url) {
        const urlPattern = /^https?:\/\/.+/i;
        return urlPattern.test(url);
    }

    /**
     * Displays inline error message.
     */
    showError(message) {
        this._errorContainer.innerText = message;
        this._errorContainer.style.display = "block";
    }

    /**
     * Hides inline error message.
     */
    _hideError() {
        this._errorContainer.style.display = "none";
    }

    _showError(message) {
        this.showError(message);
    }

    /**
     * Cleans up all resources, event listeners, and DOM elements.
     */
    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._saveButton.shutdown(),
            this._cancelUploadButton.shutdown(),
            this._cancelButton.shutdown(),
            this._modeRadio.shutdown(),
            this._nameField.shutdown(),
            this._descriptionField.shutdown(),
            this._typeDropdown.shutdown(),
            this._urlField.shutdown(),
            this._fileInputControl.shutdown(),
            this._loadingMask.shutdown(),
            this._existingImagesComboBox.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._saveButton = undefined;
                this._cancelUploadButton = undefined;
                this._cancelButton = undefined;
                this._modeRadio = undefined;
                this._nameField = undefined;
                this._descriptionField = undefined;
                this._typeDropdown = undefined;
                this._urlField = undefined;
                this._fileInputControl = undefined;
                this._loadingMask = undefined;
                this._selectedFile = undefined;
                this._fileUploadSection.remove();
                this._fileUploadSection = undefined;
                this._uploadProgressContainer.remove();
                this._uploadProgressContainer = undefined;
                this._existingImagesComboBox = undefined;
                this._errorContainer.remove();
                this._errorContainer = undefined;
                this._modeSelectionContainer.remove();
                this._modeSelectionContainer = undefined;
                this._formSection.remove();
                this._formSection = undefined;
                this._addExistingSection.remove();
                this._addExistingSection = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._contentContainer.remove();
                this._contentContainer = undefined;
                this._urlFieldRow = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "AddImagesRenderer.shutdown() error");
            }
        );
    }

    /**
     * Renders the add images dialog.
     */
    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                // Add title
                this._contentContainer.appendChild(this._titleComponent);

                // Add mode selection radio buttons
                this._modeSelectionContainer.appendChild(this._modeRadio.renderer.rootElement);
                this._contentContainer.appendChild(this._modeSelectionContainer);

                // Helper function to create field row with label
                const createFieldRow = (labelText, field) => {
                    const row = document.createElement("field-row");
                    const labelElem = document.createElement("label");
                    labelElem.innerText = labelText + ":";
                    row.appendChild(labelElem);
                    row.appendChild(field.renderer.rootElement);
                    return row;
                };

                // Add form section (Create new) with labels
                this._formSection.appendChild(createFieldRow("Name", this._nameField));
                this._formSection.appendChild(
                    createFieldRow("Description", this._descriptionField)
                );
                this._formSection.appendChild(createFieldRow("Type", this._typeDropdown));
                this._urlFieldRow = createFieldRow("URL", this._urlField);
                this._formSection.appendChild(this._urlFieldRow);

                // Add file upload section with drop area
                this._dropAreaContent.appendChild(this._plusIcon);
                this._dropAreaContent.appendChild(this._dropAreaText);
                this._fileDropArea.appendChild(this._dropAreaContent);
                this._fileUploadSection.appendChild(this._fileDropArea);
                this._browseRow.appendChild(this._fileInputControl.renderer.rootElement);
                this._uploadProgressContainer.appendChild(this._loadingMask.renderer.rootElement);
                this._formSection.appendChild(this._fileUploadSection);
                this._contentContainer.appendChild(this._formSection);

                // Add existing section (initially hidden) with combobox
                const comboBoxRow = document.createElement("field-row");
                const comboBoxLabel = document.createElement("label");
                comboBoxLabel.innerText = "Select Images:";
                comboBoxRow.appendChild(comboBoxLabel);
                comboBoxRow.appendChild(this._existingImagesComboBox.renderer.rootElement);
                this._addExistingSection.appendChild(comboBoxRow);
                this._contentContainer.appendChild(this._addExistingSection);

                // Add content
                this._rootElem.appendChild(this._contentContainer);

                // Add upload progress container
                this._contentContainer.appendChild(this._uploadProgressContainer);

                // Add button container with error on left, buttons on right
                this._buttonContainer.appendChild(this._errorContainer);
                this._buttonContainerInner.appendChild(this._browseRow);
                this._buttonContainerInner.appendChild(
                    this._cancelUploadButton.renderer.rootElement
                );
                this._buttonContainerInner.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainerInner.appendChild(this._saveButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._buttonContainerInner);
                this._contentContainer.appendChild(this._buttonContainer);

                // Initial validation
                this._validateForm();
            }
            resolve();
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    getRootElement() {
        return this.rootElement;
    }

    get model() {
        return this._model;
    }

    getModel() {
        return this.model;
    }
}

module.exports = AddImagesRenderer;
